import React, { useState, useEffect, ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiRefreshCw } from 'react-icons/fi';
import DragHandleIcon from '../icons/DragHandleIcon';

// Define the section interface
interface ConnectSection {
  id: string;
  title: string;
  content: ReactNode;
  isActionSection?: boolean;
}

// Define the props for the draggable sections component
interface DraggableConnectSectionsProps {
  sections: ConnectSection[];
  onSectionsReordered?: (sections: ConnectSection[]) => void;
}

// SortableItem component for a single section
const SortableSection = ({ section }: { section: ConnectSection }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 0 : 0,
    opacity: isDragging ? 0.8 : 1,
    boxShadow: isDragging ? '0 10px 20px rgba(0,0,0,0.1)' : 'none',
  };

  // Determine the header background class based on whether it's an action section
  const headerBgClass = section.isActionSection
    ? 'bg-gradient-to-r from-brand-blue to-brand-link' // Action section using brand colors
    : 'bg-gradient-to-r from-slate-700 to-slate-500'; // Informative section using professional grays

  // Add border accent for action sections
  const borderAccentClass = section.isActionSection ? 'border-t-2 border-t-brand-link' : '';

  // Enhanced hover effect for action sections
  const hoverEffectClass = section.isActionSection
    ? 'hover:shadow-md hover:shadow-brand-link/15 transition-shadow duration-300'
    : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-md overflow-hidden ${
        isDragging ? 'scale-[1.02]' : ''
      } transition-all duration-200 mb-8 ${borderAccentClass} ${hoverEffectClass}`}
      {...attributes}
    >
      <div
        className={`${headerBgClass} p-4 flex items-center justify-between cursor-grab active:cursor-grabbing`}
        {...listeners}
      >
        <h3 className="text-xl font-semibold text-white">{section.title}</h3>
        <div className="text-white/70 hover:text-white transition-colors">
          <DragHandleIcon size={22} />
        </div>
      </div>
      <div className="p-5">{section.content}</div>
    </div>
  );
};

const DraggableConnectSections: React.FC<DraggableConnectSectionsProps> = ({
  sections,
  onSectionsReordered,
}) => {
  // Store the original order of sections for reset functionality
  const [originalSections] = useState<ConnectSection[]>([...sections]);
  const [sectionList, setSectionList] = useState<ConnectSection[]>(sections);

  // Sync sectionList with sections prop when it changes (e.g., when editingField state updates in parent)
  useEffect(() => {
    setSectionList(sections);
  }, [sections]);

  // Set up sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSectionList(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Call the callback if provided
        if (onSectionsReordered) {
          onSectionsReordered(newItems);
        }

        return newItems;
      });
    }
  };

  // Function to reset sections to their original order
  const handleReset = () => {
    setSectionList([...originalSections]);

    // Call the callback if provided
    if (onSectionsReordered) {
      onSectionsReordered([...originalSections]);
    }
  };

  return (
    <div className="space-y-4" style={{ position: 'relative', zIndex: 0 }}>
      <div className="bg-slate-100 p-2 rounded-lg text-slate-700 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs">
              You can drag and drop these sections to reorder them. Look for the{' '}
              <DragHandleIcon className="inline mx-1" size={15} /> icon to identify draggable
              sections.
            </p>
          </div>
          <button
            onClick={handleReset}
            className="flex items-center gap-1 bg-white/70 hover:bg-white/90 text-slate-700 px-2 py-1 rounded-md text-xs transition-all shadow-sm border border-white/40 backdrop-blur-sm"
            title="Reset to default order"
          >
            <FiRefreshCw size={12} />
            <span>Reset</span>
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={sectionList.map(section => section.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="grid grid-cols-3 gap-x-8 max-lg:grid-cols-1">
            {/* First column */}
            <div className="col-span-1 flex flex-col">
              {sectionList
                .filter((_, index) => index % 3 === 0)
                .map(section => (
                  <div key={section.id} className="w-full">
                    <SortableSection section={section} />
                  </div>
                ))}
            </div>

            {/* Second column */}
            <div className="col-span-1 flex flex-col">
              {sectionList
                .filter((_, index) => index % 3 === 1)
                .map(section => (
                  <div key={section.id} className="w-full">
                    <SortableSection section={section} />
                  </div>
                ))}
            </div>

            {/* Third column */}
            <div className="col-span-1 flex flex-col">
              {sectionList
                .filter((_, index) => index % 3 === 2)
                .map(section => (
                  <div key={section.id} className="w-full">
                    <SortableSection section={section} />
                  </div>
                ))}
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default DraggableConnectSections;
